import fileUploadIcon from '@assets/images/icons-outline/file-upload-icon.svg';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { enumValues } from '@utils/enumValues';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { truncate } from 'lodash-es';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel, useFieldIds } from '../fieldLabel/fieldLabel';
import { FileTypes } from './fileTypes';

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;
const DEFAULT_MAX_FILE_SIZE_MB = 10;
const DECIMAL_PLACES = 1;
const FILE_NAME_TRUNCATE_LENGTH = 50;
const FIRST_FILE_INDEX = 0;
const THUMBNAIL_MARGIN = 0.5;
const DEFAULT_MARGIN = 1;

/**
 * Maps file extension to accepted MIME types (for exact matching)
 */
const MIME_TYPE_MAP: Record<string, string[]> = {
  pdf: ['application/pdf'],
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  heic: ['image/heic', 'image/heif'],
  csv: ['text/csv', 'text/plain', 'application/vnd.ms-excel'],
  gif: ['image/gif'],
  json: ['application/json', 'text/plain'],
  webp: ['image/webp'],
  svg: ['image/svg+xml'],
};

const readableFileSize = (bytes: number): string => {
  if (bytes < BYTES_PER_KB) return `${bytes} bytes`;
  const kB = bytes / BYTES_PER_KB;
  const fileSizeMb = (kB / BYTES_PER_KB).toFixed(DECIMAL_PLACES);
  if (kB < BYTES_PER_KB) return `${kB.toFixed()} kB`;
  return `${fileSizeMb} MB`;
};

type Props = {
  onFileChosen: (file: File) => void;
  fileMaxSize?: number;
  acceptedTypes?: FileTypes[];
  error?: boolean;
  thumbNail?: boolean;
  disabled?: boolean;
  hide?: boolean;
  testId?: string;
  label?: string;
};

/**
 * extension to input type='file' with drag-n-drop feature and some graphical design
 */
export const DroppableFileInput = ({
  onFileChosen,
  fileMaxSize = DEFAULT_MAX_FILE_SIZE_MB,
  acceptedTypes = enumValues(FileTypes),
  error = false,
  thumbNail = false,
  disabled = false,
  hide,
  testId,
  label,
}: Props) => {
  const { t } = useTranslation('document');
  const theme = useTheme();
  const { inputId } = useFieldIds(testId ? `${testId}-input` : undefined);
  const [file, setFile] = useState<File>();
  const [draggedOver, setDraggedOver] = useState(false);
  const [fileSizeError, setFileSizeError] = useState<false | number>(false);
  const [fileTypeError, setFileTypeError] = useState<false | string>(false);

  const fileSizeOverLimit = (choosenFile: File): boolean => {
    const { size } = choosenFile;
    return Number(size) > fileMaxSize * BYTES_PER_MB;
  };

  const fileTypeMatch = (chosenFile: File): boolean => {
    const { type } = chosenFile;
    const fileMimeType = type.toLowerCase();

    // Use exact MIME type matching instead of substring matching
    return acceptedTypes.some((fileType) => {
      const acceptedMimeTypes = MIME_TYPE_MAP[fileType.toLowerCase()] || [];
      return acceptedMimeTypes.includes(fileMimeType);
    });
  };

  const onFilesDropped = (files: FileList): void => {
    if (disabled || files.length === 0) return;
    const droppedFile = files[FIRST_FILE_INDEX];

    setFile(undefined);
    setFileTypeError(false);
    setFileSizeError(false);

    if (!fileTypeMatch(droppedFile)) {
      setFileTypeError(droppedFile.type);
      return;
    }
    if (fileSizeOverLimit(droppedFile)) {
      setFileSizeError(droppedFile.size);
      return;
    }
    setFile(droppedFile);
    onFileChosen(droppedFile);
  };

  return (
    <Box>
      {label ? <FieldLabel htmlFor={inputId}>{label}</FieldLabel> : null}
      <Box
        sx={{
          minHeight: thumbNail ? '90px' : '136px',
          height: thumbNail ? '90px' : '136px',
          backgroundColor:
            !disabled && draggedOver
              ? theme.color.neutralHover
              : theme.color.surface,
          borderStyle: file ? 'solid' : 'dashed',
          borderWidth: '1px',
          borderColor:
            error || fileSizeError ? theme.color.negative : theme.color.outline,
          borderRadius: theme.borderRadius.md,
          display: hide ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '16px',
        }}
        onDrop={(e) => {
          if (!disabled) onFilesDropped(e.dataTransfer.files);
          e.preventDefault();
          setDraggedOver(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDraggedOver(true);
        }}
        onDragStart={() => setDraggedOver(true)}
        onDragEnter={() => setDraggedOver(true)}
        onDragLeave={() => setDraggedOver(false)}
        onDragEnd={() => setDraggedOver(false)}
      >
        {!file && <img src={fileUploadIcon} alt='file-upload' />}
        {file && (
          <>
            <InsertDriveFileIcon
              fontSize='large'
              color='secondary'
              sx={{ marginTop: '16px', color: theme.color.onSurfaceVariant }}
            />
            <Typography
              variant='bodyMedium'
              sx={{ marginBottom: '0px', fontWeight: 500 }}
            >
              {truncate(file.name, { length: FILE_NAME_TRUNCATE_LENGTH })}
            </Typography>
            <Typography
              variant='bodyMedium'
              sx={{ marginBottom: '0px', fontWeight: 500 }}
            >
              {t('droppableFileInput.fileSize')}: &nbsp;
              {readableFileSize(file.size)}
            </Typography>
          </>
        )}
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          <Button
            data-testid={
              testId ? `${testId}-button` : 'droppable-file-input-button'
            }
            variant='text'
            component='label'
            disabled={disabled}
            sx={{
              background: 'none',
              textDecoration: 'underline',
              cursor: 'pointer',
              m: thumbNail ? THUMBNAIL_MARGIN : DEFAULT_MARGIN,
            }}
          >
            {file
              ? t('droppableFileInput.chooseAnotherFile')
              : t(
                  `droppableFileInput.${
                    thumbNail ? 'browse' : 'clickToBrowse'
                  }`,
                )}
            <input
              id={inputId}
              hidden
              type='file'
              disabled={disabled}
              accept={`.${acceptedTypes?.join(', .')}`}
              data-testid={`${testId ?? 'droppable-file'}-drop-zone`}
              onInput={(e: ChangeEvent<HTMLInputElement & FileList>) => {
                if (e.target.files) onFilesDropped(e.target.files);
              }}
            />
          </Button>
        </Typography>
        {!file && (
          <Typography
            sx={{ fontSize: 'small' }}
            color='textSecondary'
            variant='bodySmall'
            data-testid='document-max-file-size'
          >
            {t(`droppableFileInput.${thumbNail ? 'max' : 'maxDocumentSize'}`, {
              fileMaxSize: fileMaxSize.toString(),
            })}
          </Typography>
        )}
        {error && (
          <Typography
            sx={{ fontSize: 'small' }}
            color='error'
            variant='bodySmall'
            data-testid='document-validation-error'
          >
            {t('droppableFileInput.documentRequired')}
          </Typography>
        )}
        {fileSizeError && (
          <Typography
            sx={{ fontSize: 'small' }}
            color='error'
            variant='bodySmall'
            data-testid='document-validation-file-size-error'
          >
            {t('droppableFileInput.fileIsAboveMaxSize', {
              size: readableFileSize(fileSizeError),
            })}
          </Typography>
        )}
        {fileTypeError && (
          <Typography
            sx={{ fontSize: 'small' }}
            color='error'
            variant='bodySmall'
            data-testid='document-validation-file-type-error'
          >
            {t('droppableFileInput.fileIsUnsupportedType', {
              type: fileTypeError,
              acceptedTypes,
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DroppableFileInput;
